import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@project/lib/data-client";
import { AdminShell } from "@project/admin/components/admin-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Badge } from "@project/components/ui/badge";
import { Trash2, Plus, ShieldCheck } from "lucide-react";
import { formatDate } from "@project/lib/date-format";

type AppRole =
  | "super_admin"
  | "admin"
  | "donation_manager"
  | "accountant"
  | "resident_manager"
  | "content_manager";
const ROLES: { value: AppRole; label: string }[] = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "donation_manager", label: "Donation Manager" },
  { value: "accountant", label: "Accountant" },
  { value: "resident_manager", label: "Resident Manager" },
  { value: "content_manager", label: "Content Manager" },
];

type Row = {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
  email?: string;
  full_name?: string;
};

export default function Page() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<AppRole>("admin");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data: roles, error } = await supabase
      .from("user_roles")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    const ids = Array.from(new Set((roles ?? []).map(r => r.user_id)));
    const { data: profiles } = ids.length
      ? await supabase
          .from("profiles")
          .select("id, email, full_name")
          .in("id", ids)
      : { data: [] as any[] };
    const map = new Map((profiles ?? []).map((p: any) => [p.id, p]));
    setRows(
      (roles ?? []).map((r: any) => ({
        ...r,
        email: map.get(r.user_id)?.email,
        full_name: map.get(r.user_id)?.full_name,
      }))
    );
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim()) {
      return toast.error("Enter a user ID");
    }
    setBusy(true);
    const { error } = await supabase
      .from("user_roles")
      .insert({ user_id: userId.trim(), role });
    setBusy(false);
    if (error) {
      return toast.error(error.message);
    }
    toast.success("Role assigned");
    setUserId("");
    void load();
  };

  const remove = async (id: string) => {
    if (!confirm("Revoke this role?")) {
      return;
    }
    const { error } = await supabase.from("user_roles").delete().eq("id", id);
    if (error) {
      return toast.error(error.message);
    }
    toast.success("Revoked");
    void load();
  };

  return (
    <AdminShell
      title="Staff Roles"
      subtitle="Assign admin & staff permissions (Super Admin only)"
    >
      <div className="space-y-6">
        <form
          onSubmit={add}
          className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5"
        >
          <div className="mb-3 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold">Assign Role</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr,200px,auto]">
            <div>
              <Label>User ID (UUID)</Label>
              <Input
                value={userId}
                onChange={e => setUserId(e.target.value)}
                placeholder="auth user id"
                className="bg-muted/40 rounded-xl border-transparent"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={v => setRole(v as AppRole)}>
                <SelectTrigger className="bg-muted/40 rounded-xl border-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={busy}
                className="w-full gap-1 rounded-xl"
              >
                <Plus className="h-4 w-4" /> Assign
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Tip: a new staff member should sign up at /admin first; copy their
            User ID from the auth dashboard, then assign the role here.
          </p>
        </form>

        <div className="-mx-3 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm sm:mx-0">
          <Table className="min-w-[640px]">
            <TableHeader className="bg-muted/60">
              <TableRow>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Role</TableHead>
                <TableHead className="font-semibold">Assigned</TableHead>
                <TableHead className="text-right font-semibold">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-muted-foreground"
                  >
                    Loading…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-muted-foreground"
                  >
                    No role assignments yet.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r, i) => (
                  <TableRow
                    key={r.id}
                    className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}
                  >
                    <TableCell>{r.full_name ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {r.email ?? r.user_id.slice(0, 8) + "…"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {r.role.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(r.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(r.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminShell>
  );
}
